import React, { useEffect, useRef, useState } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import TarefaPrioridadeBadge from './TarefaPrioridadeBadge';
import {
  KanbanTarefa,
  KanbanTarefaWithProjectDeadline,
  TarefaStatus,
} from '../../types/tarefa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt,
  faBriefcase,
  faExclamationTriangle,
  faSyncAlt,
} from '@fortawesome/free-solid-svg-icons';
import { getDeadlineStatus, getTaskOverdueStatus } from '../../utils/dateUtils';
import { useSubtarefas } from '../../hooks/useSubtarefas';
import { getFrequenciaRecorrenciaLabel } from '../../constants/frequenciaRecorrencia';
import './styles.scss';

interface TarefaCardProps {
  tarefa: KanbanTarefa;
  index: number;
  onClick?: (tarefa: KanbanTarefa) => void;
}

const getStatusColor = (status: TarefaStatus) => {
  const colors: { [key in TarefaStatus]: string } = {
    BACKLOG: '#E2E8F0',
    TODO: '#FEF3C7',
    IN_PROGRESS: '#BFDBFE',
    IN_REVIEW: '#DDD6FE',
    DONE: '#BBF7D0',
  };
  return colors[status];
};


// Function to get card style based on deadline status
const getCardStyle = (tarefa: KanbanTarefa): string => {
  // A task whose own deadline has already passed takes priority over the
  // project-deadline-proximity styling below — it's the more urgent, more
  // actionable signal, and applies even without a project deadline set.
  if (getTaskOverdueStatus(tarefa.prazoReal, tarefa.status).isPastDue) {
    return 'task-past-due';
  }

  // Cast to our extended interface that includes project deadline
  const tarefaWithPrazo = tarefa as KanbanTarefaWithProjectDeadline;

  // If we don't have a project deadline, we can't determine status
  if (!tarefaWithPrazo.projeto?.prazo) {
    return '';
  }

  const status = getDeadlineStatus(
    tarefa.prazoReal,
    tarefaWithPrazo.projeto.prazo
  );

  if (status.isOverdue) {
    return 'task-overdue';
  }

  if (status.isApproaching) {
    return status.daysRemaining !== null && status.daysRemaining <= 2
      ? 'task-critical'
      : 'task-approaching';
  }

  return '';
};

// Helper function to format date
const formatDate = (dateString: string) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('pt-PT');
};

const TarefaCard: React.FC<TarefaCardProps> = React.memo(({ tarefa, index, onClick }) => {
  // Cast to our extended interface that includes project deadline
  const tarefaWithPrazo = tarefa as KanbanTarefaWithProjectDeadline;
  const deadlineStatus = getDeadlineStatus(
    tarefa.prazoReal,
    tarefaWithPrazo.projeto?.prazo
  );
  const overdueStatus = getTaskOverdueStatus(tarefa.prazoReal, tarefa.status);
  const cardStyleClass = getCardStyle(tarefa);

  // Só pede as subtarefas quando o card entra em viewport — evita disparar
  // um GET por cada card montado quando o board tem muitas tarefas.
  const [isVisible, setIsVisible] = useState(false);
  const cardNodeRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isVisible) return;
    const node = cardNodeRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [isVisible]);

  const { isDividida, totalPercentual } = useSubtarefas(tarefa.id, {
    enabled: isVisible,
  });

  return (
    <Draggable draggableId={tarefa.uniqueId} index={index}>
      {(provided, snapshot) => (
        <div
          ref={(node) => {
            provided.innerRef(node);
            cardNodeRef.current = node;
          }}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={() => onClick?.(tarefa)}
          style={{
            userSelect: 'none',
            padding: '12px',
            margin: '0 0 8px 0',
            borderRadius: '6px',
            backgroundColor: getStatusColor(tarefa.status),
            boxShadow: snapshot.isDragging
              ? '0 5px 10px rgba(0,0,0,0.2)'
              : '0 2px 4px rgba(0,0,0,0.1)',
            fontFamily: 'Roboto, sans-serif',
            transform: snapshot.isDragging ? 'rotate(2deg)' : 'none',
            opacity: snapshot.isDragging ? 0.9 : 1,
            ...provided.draggableProps.style,
          }}
          className={`tarefa-card-wrapper ${cardStyleClass}`}
        >
          <h3
            style={{ fontSize: '16px', marginBottom: '8px', color: '#1F2937' }}
            className="tarefa-title"
          >
            {tarefa.descricao}
          </h3>
          <div
            style={{ fontSize: '14px', color: '#4B5563' }}
            className="tarefa-content"
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '4px',
              }}
              className="tarefa-meta"
            >
              <div className="d-flex align-items-center gap-1">
                <TarefaPrioridadeBadge prioridade={tarefa.prioridade} />
                {tarefa.recorrente && (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id={`recorrente-tooltip-${tarefa.id}`}>
                        Tarefa recorrente
                        {tarefa.frequenciaRecorrencia
                          ? ` (${getFrequenciaRecorrenciaLabel(tarefa.frequenciaRecorrencia)})`
                          : ''}
                      </Tooltip>
                    }
                  >
                    <span className="badge bg-info text-dark">
                      <FontAwesomeIcon icon={faSyncAlt} />
                    </span>
                  </OverlayTrigger>
                )}
              </div>
              <span className="tarefa-date">
                <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                Prazo: {formatDate(tarefa.prazoReal)}
                {/* Add deadline indicator */}
                {overdueStatus.isPastDue ? (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id={`deadline-tooltip-${tarefa.id}`}>
                        {`O prazo desta tarefa (${overdueStatus.formattedTaskDate}) já passou há ${overdueStatus.daysOverdue} dia(s)`}
                      </Tooltip>
                    }
                  >
                    <span className="deadline-badge deadline-past-due">
                      <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        className="me-1"
                      />
                      Atrasada
                    </span>
                  </OverlayTrigger>
                ) : (
                  deadlineStatus.isApproaching && (
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id={`deadline-tooltip-${tarefa.id}`}>
                          {deadlineStatus.isOverdue
                            ? `Esta tarefa excede o prazo do projeto (${deadlineStatus.formattedProjectDate})`
                            : `O prazo desta tarefa (${deadlineStatus.formattedTaskDate}) fica a apenas ${deadlineStatus.daysRemaining} dia(s) do prazo do projeto (${deadlineStatus.formattedProjectDate})`}
                        </Tooltip>
                      }
                    >
                      <span
                        className={`deadline-badge ${
                          deadlineStatus.isOverdue
                            ? 'deadline-overdue'
                            : deadlineStatus.daysRemaining !== null &&
                              deadlineStatus.daysRemaining <= 2
                            ? 'deadline-critical'
                            : 'deadline-approaching'
                        }`}
                      >
                        <FontAwesomeIcon
                          icon={faExclamationTriangle}
                          className="me-1"
                        />
                        {deadlineStatus.daysRemaining !== null
                          ? `${deadlineStatus.daysRemaining}d`
                          : '!'}
                      </span>
                    </OverlayTrigger>
                  )
                )}
              </span>
            </div>

            {/* Add working days information */}
            {tarefa.workingDays != null && (
              <div
                className="tarefa-working-days"
                style={{ marginTop: '4px', fontSize: '13px' }}
              >
                <FontAwesomeIcon icon={faBriefcase} className="me-1" />
                {tarefa.workingDays} dia(s) útil(eis)
              </div>
            )}

            <div style={{ marginTop: '8px' }} className="tarefa-footer">
              <div className="tarefa-projeto d-flex align-items-center justify-content-between">
                <span>{tarefa.projeto.designacao}</span>
                {isDividida && (
                  <span
                    className={`badge ${
                      totalPercentual >= 100
                        ? 'bg-success'
                        : totalPercentual >= 50
                        ? 'bg-warning'
                        : 'bg-danger'
                    }`}
                  >
                    {totalPercentual}%
                  </span>
                )}
              </div>
              <div
                style={{
                  fontSize: '13px',
                  color: '#6B7280',
                  marginTop: '4px',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
                className="tarefa-users"
              >
                {tarefa.users && tarefa.users.length > 0
                  ? tarefa.users.map((user) => user.name).join(', ')
                  : 'Não atribuída'}
              </div>
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );
});

TarefaCard.displayName = 'TarefaCard';

export default TarefaCard;
